/**
 * React hook for SignalR-based real-time orchestration events.
 * This is an alternative to useOrchestrateSse that uses SignalR/WebSocket
 * instead of Server-Sent Events for improved scalability.
 * 
 * @module hooks/useSignalR
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import {
  SignalRService,
  SignalRConnectionState,
  OrchestrationEventMap,
  OrchestrationHubEvent,
  isSignalREnabled,
  getSignalRService
} from '@/lib/signalRService'

// ============================================================================
// Types
// ============================================================================

/**
 * Options for the useSignalR hook
 */
export interface UseSignalROptions {
  /** Session ID to subscribe to */
  sessionId?: string
  /** Whether to connect (default: false) */
  enabled?: boolean
  /** Use a shared singleton instance (default: true) */
  useSharedInstance?: boolean
  /** Event callbacks */
  onSessionStatusChanged?: (payload: OrchestrationEventMap['SessionStatusChanged']) => void
  onProgressUpdate?: (payload: OrchestrationEventMap['ProgressUpdate']) => void
  onToolCalled?: (payload: OrchestrationEventMap['ToolCalled']) => void
  onClarificationNeeded?: (payload: OrchestrationEventMap['ClarificationNeeded']) => void
  onSessionComplete?: (payload: OrchestrationEventMap['SessionComplete']) => void
  onSessionError?: (payload: OrchestrationEventMap['SessionError']) => void
  onChatMessageStart?: (payload: OrchestrationEventMap['ChatMessageStart']) => void
  onChatToken?: (payload: OrchestrationEventMap['ChatToken']) => void
  onChatToolCall?: (payload: OrchestrationEventMap['ChatToolCall']) => void
  onChatToolResult?: (payload: OrchestrationEventMap['ChatToolResult']) => void
  onChatComplete?: (payload: OrchestrationEventMap['ChatComplete']) => void
  onChatError?: (payload: OrchestrationEventMap['ChatError']) => void
  onChatEscalationStart?: (payload: OrchestrationEventMap['ChatEscalationStart']) => void
  onChatReasoning?: (payload: OrchestrationEventMap['ChatReasoning']) => void
  onPolicyDecision?: (payload: OrchestrationEventMap['PolicyDecision']) => void
  onRetryAttempt?: (payload: OrchestrationEventMap['RetryAttempt']) => void
  onGuardianIteration?: (payload: OrchestrationEventMap['GuardianIteration']) => void
  onSubTaskCompleted?: (payload: OrchestrationEventMap['SubTaskCompleted']) => void
  onChatContextSnapshot?: (payload: OrchestrationEventMap['ChatContextSnapshot']) => void
  /** Connection state callbacks */
  onConnectionStateChange?: (state: SignalRConnectionState) => void
  onReconnecting?: (error?: Error) => void
  onReconnected?: (connectionId?: string) => void
  onClose?: (error?: Error) => void
}

/**
 * Return value from the useSignalR hook
 */
export interface UseSignalRReturn {
  /** Current connection state */
  connectionState: SignalRConnectionState
  /** Whether SignalR feature is enabled */
  isEnabled: boolean
  /** Error message if connection failed */
  error: string | null
  /** Subscribe to a session */
  subscribe: (sessionId: string) => Promise<void>
  /** Unsubscribe from a session */
  unsubscribe: (sessionId: string) => Promise<void>
  /** Manually connect */
  connect: () => Promise<void>
  /** Manually disconnect */
  disconnect: () => Promise<void>
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * React hook for managing SignalR connections and subscriptions.
 * 
 * @example
 * ```tsx
 * // Basic usage - subscribe to a session
 * const { connectionState, error } = useSignalR({
 *   sessionId: 'session-123',
 *   enabled: true,
 *   onChatToken: (payload) => {
 *     setResponse(prev => prev + payload.token)
 *   },
 *   onChatComplete: (payload) => {
 *     console.log('Response complete:', payload.response)
 *   }
 * })
 * 
 * // Manual control
 * const { connect, disconnect, subscribe, unsubscribe } = useSignalR({
 *   onSessionStatusChanged: (payload) => {
 *     console.log('Session status:', payload.status)
 *   }
 * })
 * 
 * // Connect and subscribe manually
 * await connect()
 * await subscribe('session-456')
 * ```
 */
export function useSignalR(options: UseSignalROptions = {}): UseSignalRReturn {
  const {
    sessionId,
    enabled = false,
    useSharedInstance = true,
  } = options

  // Keep the latest callbacks in a ref so the connection/subscription lifecycle never depends on their identity.
  // Callers pass fresh handler closures every render; if connect()/the auto-connect effect depended on them, the
  // effect would tear down and re-subscribe on EVERY render. Streaming re-renders once per token, so the SignalR
  // group flapped (Unsubscribe→Subscribe) and handlers were unregistered/re-registered hundreds of times per turn,
  // dropping tokens and the terminal ChatComplete mid-stream (chatbot-streaming D7). Registered handlers below
  // dispatch through this ref, so the registration identity stays stable.
  const callbacksRef = useRef(options)
  callbacksRef.current = options

  const [connectionState, setConnectionState] = useState<SignalRConnectionState>('disconnected')
  const [error, setError] = useState<string | null>(null)
  
  const serviceRef = useRef<SignalRService | null>(null)
  const cleanupFnsRef = useRef<(() => void)[]>([])

  // Check if SignalR is enabled
  const isEnabled = isSignalREnabled()

  // Get or create the service instance. Connection-level callbacks dispatch through the ref so this stays stable
  // (a new onReconnecting/onClose closure each render must not churn the connect effect — see D7 note above).
  const getService = useCallback((): SignalRService => {
    const serviceOptions = {
      onStateChange: setConnectionState,
      onReconnecting: (e?: Error) => callbacksRef.current.onReconnecting?.(e),
      onReconnected: (id?: string) => callbacksRef.current.onReconnected?.(id),
      onClose: (e?: Error) => callbacksRef.current.onClose?.(e),
    }

    if (useSharedInstance) {
      return getSignalRService(serviceOptions)
    }

    if (!serviceRef.current) {
      serviceRef.current = new SignalRService(serviceOptions)
    }

    return serviceRef.current
  }, [useSharedInstance])

  // Setup event handlers. Each registration is a STABLE wrapper that dispatches to the latest callback via the ref,
  // so the registration set never changes with handler identity (deps: []). This is what stops the per-render
  // unregister/re-register + Subscribe/Unsubscribe churn that dropped streamed events (D7).
  const setupEventHandlers = useCallback((service: SignalRService): void => {
    // Clear any prior registrations (re-run only on real connect / session change now, not every render).
    cleanupFnsRef.current.forEach(cleanup => cleanup())
    cleanupFnsRef.current = []

    const on = <E extends OrchestrationHubEvent>(
      event: E,
      pick: (o: UseSignalROptions) => ((payload: OrchestrationEventMap[E]) => void) | undefined,
    ): void => {
      cleanupFnsRef.current.push(
        service.on(event, (payload) => pick(callbacksRef.current)?.(payload)),
      )
    }

    on('SessionStatusChanged', o => o.onSessionStatusChanged)
    on('ProgressUpdate', o => o.onProgressUpdate)
    on('ToolCalled', o => o.onToolCalled)
    on('ClarificationNeeded', o => o.onClarificationNeeded)
    on('SessionComplete', o => o.onSessionComplete)
    on('SessionError', o => o.onSessionError)
    on('ChatMessageStart', o => o.onChatMessageStart)
    on('ChatToken', o => o.onChatToken)
    on('ChatToolCall', o => o.onChatToolCall)
    on('ChatToolResult', o => o.onChatToolResult)
    on('ChatComplete', o => o.onChatComplete)
    on('ChatError', o => o.onChatError)
    on('ChatEscalationStart', o => o.onChatEscalationStart)
    on('ChatReasoning', o => o.onChatReasoning)
    on('PolicyDecision', o => o.onPolicyDecision)
    on('RetryAttempt', o => o.onRetryAttempt)
    on('GuardianIteration', o => o.onGuardianIteration)
    on('SubTaskCompleted', o => o.onSubTaskCompleted)
    on('ChatContextSnapshot', o => o.onChatContextSnapshot)
  }, [])

  // Connect to SignalR
  const connect = useCallback(async (): Promise<void> => {
    if (!isEnabled) {
      setError('SignalR is not enabled')
      return
    }

    setError(null)
    const service = getService()
    
    try {
      setupEventHandlers(service)
      await service.start()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect'
      setError(message)
      throw err
    }
  }, [isEnabled, getService, setupEventHandlers])

  // Disconnect from SignalR
  const disconnect = useCallback(async (): Promise<void> => {
    // Clean up handlers
    cleanupFnsRef.current.forEach(cleanup => cleanup())
    cleanupFnsRef.current = []

    if (useSharedInstance) {
      // Don't stop shared instance, just unsubscribe
      return
    }

    if (serviceRef.current) {
      await serviceRef.current.stop()
      serviceRef.current = null
    }
  }, [useSharedInstance])

  // Subscribe to a session
  const subscribe = useCallback(async (sid: string): Promise<void> => {
    const service = getService()
    await service.subscribe(sid)
  }, [getService])

  // Unsubscribe from a session
  const unsubscribe = useCallback(async (sid: string): Promise<void> => {
    const service = getService()
    await service.unsubscribe(sid)
  }, [getService])

  // Auto-connect and subscribe when enabled
  useEffect(() => {
    if (!isEnabled || !enabled) {
      return
    }

    let mounted = true

    const setup = async () => {
      try {
        await connect()
        
        if (mounted && sessionId) {
          await subscribe(sessionId)
        }
      } catch (err) {
        if (mounted) {
          const message = err instanceof Error ? err.message : 'Connection failed'
          setError(message)
        }
      }
    }

    setup()

    return () => {
      mounted = false
      
      if (sessionId) {
        unsubscribe(sessionId).catch(console.error)
      }
      
      disconnect().catch(console.error)
    }
  }, [isEnabled, enabled, sessionId, connect, disconnect, subscribe, unsubscribe])

  // Notify state changes (dispatch through the ref so this never re-runs on a fresh handler closure).
  useEffect(() => {
    callbacksRef.current.onConnectionStateChange?.(connectionState)
  }, [connectionState])

  return {
    connectionState,
    isEnabled,
    error,
    subscribe,
    unsubscribe,
    connect,
    disconnect
  }
}

/**
 * Check if SignalR should be used based on the feature flag.
 * This can be used to conditionally render different UIs or use different hooks.
 * 
 * @example
 * ```tsx
 * if (useSignalREnabled()) {
 *   return <SignalRChatComponent />
 * }
 * return <SSEChatComponent />
 * ```
 */
export function useSignalREnabled(): boolean {
  return isSignalREnabled()
}

export default useSignalR
